/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  OnDestroy,
  ChangeDetectionStrategy
} from '@angular/core';
import {
    Route,
    Router
} from '@angular/router';
import {SideBarMenuItemModel} from './side-bar-menu-item/side-bar-menu-item.model';
import {RoutingMenuItemModel} from '../utils/routing-menu-item.model';
import {first} from 'rxjs/operators';
import {
    forkJoin,
    Subscription
} from 'rxjs';
import {AppService} from '../../app.service';
import {ReplayableRouterEventsService} from '../services/replayable-router-events.service';
import {DependencyLoadNotifierService} from '../services/dependency-load-notifier/dependency-load-notifier.service';
import {UserService} from '../services/user.service';
import {FeatureToggleService} from '../guards/feature-toggle/feature-toggle.service';

@Component({
    selector: 'app-side-bar-menu',
    templateUrl: './side-bar-menu.component.html',
    styleUrls: ['./side-bar-menu.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SideBarMenuComponent implements OnDestroy {
    showComponent = true;
    public menuItems: SideBarMenuItemModel[] = [];
    public menuItemsCollapsed: SideBarMenuItemModel[] = [];
    public isExpanded = false;
    private readonly ROUTE_DATA_MENU_ITEM = 'menuItem';
    private subscriptions = new Subscription();

    constructor(private router: Router,
                private userService: UserService,
                private featureToggleService: FeatureToggleService,
                private dependencyLoadNotifier: DependencyLoadNotifierService,
                private appService: AppService,
                private routerService: ReplayableRouterEventsService) {
        this.subscriptions.add(forkJoin([
            this.routerService.onNavigationStart.pipe(first()),
            this.dependencyLoadNotifier.onReady
        ])
                .subscribe(() => {
                    this.showComponent = !this.userService.isFirstAccess;
                    this.menuItems = this.menuItemsCollapsed = this.getRouteTree(this.router.config);
                }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    toggleExpanded(): void {
        this.isExpanded = !this.isExpanded;
        if (!this.isExpanded) {
            this.menuItems.forEach((item) => item.isExpanded = this.isExpanded);
        }
    }

    getRouteTree(routes: Route[], basePath: string = '/'): SideBarMenuItemModel[] {
        const menuItems: SideBarMenuItemModel[] = [];

        for (const route of routes) {
            // The route doesn't have a menu item
            if (route.data == null || !route.data.hasOwnProperty(this.ROUTE_DATA_MENU_ITEM)) {
                continue;
            }

            // The route has disabled features
            if (route.data.featureToggles != null && !this.featureToggleService.isEnabled(route.data.featureToggles)) {
                continue;
            }

            // The route doesn't allow the user
            if (route.data['permissions'] != null && (this.userService.isFirstAccess
                    || !this.userService.hasPermissions(route.data['permissions'], 'any', route.data['context']))) {
                continue;
            }

            const childConfig = (route as any)._loadedConfig;
            let childrenMenuItems: SideBarMenuItemModel[];
            const thisPath = `${basePath}${route.path}`;

            if (route.children != null && route.children.length > 0) {
                childrenMenuItems = this.getRouteTree(route.children, `${thisPath}/`);
            } else if (childConfig != null && childConfig.routes.length > 0) {
                childrenMenuItems = this.getRouteTree(childConfig.routes, `${thisPath}/`);
            }

            const routeMenuItem: RoutingMenuItemModel = route.data[this.ROUTE_DATA_MENU_ITEM];

            const menuItem: SideBarMenuItemModel = {
                label: routeMenuItem.label,
                url: this.removeTrailingSlashes(thisPath),
                index: routeMenuItem.index != null ? routeMenuItem.index : Number.MAX_SAFE_INTEGER,
                icon: routeMenuItem.icon != null ? routeMenuItem.icon : '',
                isExpanded: false,
                children: childrenMenuItems
            };

            menuItems.push(menuItem);
        }

        if (menuItems.length > 0) {
            return menuItems.sort((a, b) => a.index - b.index);
        }

        return null;
    }

    removeTrailingSlashes(path: string): string {
        return path.replace(/\/+$/, '');
    }

    getTooltipText(): string {
        let tooltipString;
        if (this.isExpanded) {
            tooltipString = 'Collapse Navigation Menu';
        } else {
            tooltipString = 'Expand Navigation Menu';
        }
        return tooltipString;
    }
}
